import { useAuthStore } from '@/stores/authStore';

// The four flags the backend stores. There is no view_any: the backend's viewAny reads can_view,
// so a can_view_any key would never arrive and would refuse every non-developer.
type Ability = 'view' | 'create' | 'update' | 'delete';

/**
 * Composable for checking the current user's model-level permissions and
 * their explicit page grants. Developers bypass all checks.
 */
export function usePermissions() {
  const authStore = useAuthStore();

  function can(ability: Ability, model: string): boolean {
    if (authStore.userData?.is_developer) {
      return true;
    }

    const modelPermissions = authStore.userData?.permissions?.[model];
    if (!modelPermissions) {
      return false;
    }

    const key = `can_${ability}` as keyof typeof modelPermissions;
    return modelPermissions[key] ?? false;
  }

  /**
   * True when the current user has the named page granted — drives nav-bar
   * visibility and route gating. The auth store's `granted_page_names`
   * array carries that result over from /api/user. Developers bypass.
   */
  function canAccessPage(pageName: string): boolean {
    if (authStore.userData?.is_developer) {
      return true;
    }
    const grantedNames = authStore.userData?.granted_page_names ?? [];
    return grantedNames.includes(pageName);
  }

  /**
   * Lower-level helper exposed for tests: skips the developer bypass and
   * returns the explicit-grant check against the auth store's
   * granted_page_names array.
   */
  function isPageGrantedForUser(pageName: string): boolean {
    const grantedNames = authStore.userData?.granted_page_names ?? [];
    return grantedNames.includes(pageName);
  }

  return { can, canAccessPage, isPageGrantedForUser };
}
